import { getCustomerQuickLookup } from '../utils/apiClient';

// Constants for billing calculations
const AVERAGE_MINUTES_PER_CALL = 20;
const TRIAL_USER_PATIENT_LIMIT = 8;

/**
 * Response structure for billing restriction checks
 * @typedef {Object} BillingCheckResponse
 * @property {boolean} canProceed - Whether the operation can proceed
 * @property {string} message - Descriptive message about the check result
 * @property {number} requiredUnits - Number of units required for the operation
 * @property {number} remainingUnits - Number of units remaining in account
 * @property {boolean} hasActiveSubscription - Whether user has active subscription
 */

/**
 * Checks if user has sufficient units for a given number of calls
 * @param {number} numberOfCalls - Number of calls to be made
 * @returns {Promise<BillingCheckResponse>} Response object with check results
 */
export const checkUnitsAvailability = async (numberOfCalls) => {
  try {
    // Input validation
    if (!numberOfCalls || numberOfCalls <= 0) {
      return {
        canProceed: false,
        message: "Please select patients to call before proceeding.",
        requiredUnits: 0,
        remainingUnits: 0,
        hasActiveSubscription: false
      };
    }

    // Get customer billing status
    const customerStatus = await getCustomerQuickLookup();
    
    const {
      has_active_subscription,
      subscription_status,
      remaining_units,
      access_allowed
    } = customerStatus;

    // Calculate required units for the operation
    const requiredUnits = AVERAGE_MINUTES_PER_CALL * numberOfCalls;

    // Base response object
    const response = {
      canProceed: false,
      message: "",
      requiredUnits,
      remainingUnits: remaining_units || 0,
      hasActiveSubscription: has_active_subscription || false
    };

    // Check if subscription is active
    if (!has_active_subscription || subscription_status !== 'active') {
      response.message = "Your account does not have an active calling plan. Please contact your administrator to activate calling services.";
      return response;
    }

    // Check for expired units scenario - active subscription but no remaining units
    // In this case, allow to proceed with warning about overage charges
    if (has_active_subscription && remaining_units === 0) {
      response.canProceed = true;
      response.isExpiredUnits = true;
      response.message = "You've reached your unit limit for this period. If you continue, additional overage charges will be applied to your account.";
      return response;
    }

    // Check if access is allowed by the API
    if (!access_allowed) {
      response.message = "Calling access is currently restricted. Please contact your administrator for assistance.";
      return response;
    }

    // Check if user has sufficient units
    const unitsAfterOperation = remaining_units - requiredUnits;

    if (unitsAfterOperation >= 0) {
      response.canProceed = true;
      response.message = `Ready to proceed with ${numberOfCalls} call${numberOfCalls > 1 ? 's' : ''}. You have sufficient calling minutes available.`;
    } else {
      const shortfall = Math.abs(unitsAfterOperation);
      const callsToRemove = Math.ceil(shortfall / AVERAGE_MINUTES_PER_CALL);
      response.message = `Insufficient calling minutes available. Please reduce your call list by ${callsToRemove} patient${callsToRemove > 1 ? 's' : ''} to proceed, or contact your administrator to add more minutes to your account.`;
    }

    return response;

  } catch (error) {
    console.error('Error in checkUnitsAvailability:', error);
    return {
      canProceed: false,
      message: "Unable to verify calling minutes availability. Please try again or contact your administrator.",
      requiredUnits: AVERAGE_MINUTES_PER_CALL * (numberOfCalls || 0),
      remainingUnits: 0,
      hasActiveSubscription: false
    };
  }
};

/**
 * Checks if user has any active subscription without specific unit calculations
 * @returns {Promise<BillingCheckResponse>} Response object with subscription status
 */
export const checkSubscriptionStatus = async () => {
  try {
    const customerStatus = await getCustomerQuickLookup();
    
    const {
      has_active_subscription,
      subscription_status,
      remaining_units,
      access_allowed,
      message
    } = customerStatus;

    const response = {
      canProceed: has_active_subscription && subscription_status === 'active' && access_allowed,
      message: message || `Subscription status: ${subscription_status || 'inactive'}`,
      requiredUnits: 0,
      remainingUnits: remaining_units || 0,
      hasActiveSubscription: has_active_subscription || false
    };

    if (!response.canProceed) {
      if (!has_active_subscription) {
        response.message = "Your account does not have an active calling plan. Please contact your administrator to access calling features.";
      } else if (subscription_status !== 'active') {
        response.message = `Your calling plan is currently ${subscription_status}. Please contact your administrator to activate calling services.`;
      } else if (!access_allowed) {
        response.message = "Calling access is currently restricted. Please contact your administrator for assistance.";
      }
    }

    return response;

  } catch (error) {
    console.error('Error in checkSubscriptionStatus:', error);
    return {
      canProceed: false,
      message: "Unable to verify your calling plan status. Please try again or contact your administrator.",
      requiredUnits: 0,
      remainingUnits: 0,
      hasActiveSubscription: false
    };
  }
};

/**
 * Gets the average minutes per call constant
 * @returns {number} Average minutes per call
 */
export const getAverageMinutesPerCall = () => {
  return AVERAGE_MINUTES_PER_CALL;
};

/**
 * Gets the trial user patient limit constant
 * @returns {number} Trial user patient limit
 */
export const getTrialUserPatientLimit = () => {
  return TRIAL_USER_PATIENT_LIMIT;
};

/**
 * Export the trial user constants directly
 */
export { TRIAL_USER_PATIENT_LIMIT };

/**
 * Calculates required units for a given number of calls
 * @param {number} numberOfCalls - Number of calls
 * @returns {number} Required units
 */
export const calculateRequiredUnits = (numberOfCalls) => {
  return AVERAGE_MINUTES_PER_CALL * (numberOfCalls || 0);
};

/**
 * Checks if user has low calling minutes (less than average per call)
 * Shows warning about potential extra charges
 * @returns {Promise<BillingCheckResponse>} Response object with low minutes warning
 */
export const checkLowMinutesWarning = async () => {
  try {
    const customerStatus = await getCustomerQuickLookup();
    
    const {
      has_active_subscription,
      subscription_status,
      remaining_units,
      access_allowed
    } = customerStatus;

    const response = {
      canProceed: true, // Allow to proceed but with warning
      message: "",
      requiredUnits: 0,
      remainingUnits: remaining_units || 0,
      hasActiveSubscription: has_active_subscription || false
    };

    // Check if subscription is active first
    if (!has_active_subscription || subscription_status !== 'active' || !access_allowed) {
      response.canProceed = false;
      response.message = "Your account does not have an active calling plan. Please contact your administrator to activate calling services.";
      return response;
    }

    // Check if remaining units are less than average per call
    if (remaining_units < AVERAGE_MINUTES_PER_CALL) {
      response.message = `You have less than ${AVERAGE_MINUTES_PER_CALL} minutes remaining in your calling plan. Additional calls may incur extra charges. Please confirm with your administrator before proceeding.`;
    } else {
      response.message = "Sufficient calling minutes available.";
    }

    return response;

  } catch (error) {
    console.error('Error in checkLowMinutesWarning:', error);
    return {
      canProceed: false,
      message: "Unable to verify calling minutes status. Please try again or contact your administrator.",
      requiredUnits: 0,
      remainingUnits: 0,
      hasActiveSubscription: false
    };
  }
};

/**
 * Simple check for active subscription - used for page access control
 * @returns {Promise<BillingCheckResponse>} Response object with subscription access status
 */
export const checkPageAccess = async () => {
  try {
    const customerStatus = await getCustomerQuickLookup();
    
    const {
      has_active_subscription,
      subscription_status,
      remaining_units,
      access_allowed
    } = customerStatus;

    const hasAccess = has_active_subscription && subscription_status === 'active' && access_allowed;

    const response = {
      canProceed: hasAccess,
      message: "",
      requiredUnits: 0,
      remainingUnits: remaining_units || 0,
      hasActiveSubscription: has_active_subscription || false
    };

    if (hasAccess) {
      response.message = "Access granted to calling features.";
    } else {
      if (!has_active_subscription) {
        response.message = "Access denied. Your account does not have an active calling plan. Please contact your administrator to access calling features.";
      } else if (subscription_status !== 'active') {
        response.message = `Access denied. Your calling plan is currently ${subscription_status}. Please contact your administrator to activate calling services.`;
      } else if (!access_allowed) {
        response.message = "Access denied. Calling access is currently restricted. Please contact your administrator for assistance.";
      }
    }

    // Check for expired units scenario - active subscription but no remaining units
    if (has_active_subscription && remaining_units === 0) {
      response.canProceed = true;
      response.isExpiredUnits = true;
      response.message = "You've reached your unit limit for this period. If you continue, additional overage charges will be applied to your account.";
    }

    return response;

  } catch (error) {
    console.error('Error in checkPageAccess:', error);
    return {
      canProceed: false,
      message: "Unable to verify access permissions. Please try again or contact your administrator.",
      requiredUnits: 0,
      remainingUnits: 0,
      hasActiveSubscription: false
    };
  }
};

/**
 * Checks if user is on trial plan
 * @returns {Promise<Object>} Response object with trial status
 */
export const checkTrialStatus = async () => {
  try {
    const customerStatus = await getCustomerQuickLookup();
    
    const {
      is_trial,
      plan_name,
      has_active_subscription,
      subscription_status,
      access_allowed
    } = customerStatus;

    return {
      isTrialUser: is_trial || false,
      planName: plan_name || "Unknown Plan",
      hasActiveSubscription: has_active_subscription || false,
      subscriptionStatus: subscription_status || "unknown",
      accessAllowed: access_allowed || false
    };

  } catch (error) {
    console.error('Error in checkTrialStatus:', error);
    return {
      isTrialUser: false,
      planName: "Unknown Plan",
      hasActiveSubscription: false,
      subscriptionStatus: "unknown",
      accessAllowed: false
    };
  }
};

/**
 * Counts active batches (excludes cancelled batches)
 * @param {Array} batches - Array of batch objects
 * @returns {number} Count of active batches
 */
export const countActiveBatches = (batches) => {
  if (!Array.isArray(batches)) return 0;
  return batches.filter(batch => batch.status !== 'cancelled').length;
};

/**
 * Calculates total calls across all active batches
 * @param {Array} batches - Array of batch objects
 * @returns {number} Total calls in active batches
 */
export const getTotalActiveCalls = (batches) => {
  console.log(`🔍 TRIAL DEBUG: getTotalActiveCalls called with:`, batches);
  console.log(`🔍 TRIAL DEBUG: Is array?`, Array.isArray(batches));
  console.log(`🔍 TRIAL DEBUG: Array length:`, batches?.length);
  
  if (!Array.isArray(batches)) {
    console.log(`🔍 TRIAL DEBUG: Not an array, returning 0`);
    return 0;
  }
  
  console.log(`🔍 TRIAL DEBUG: Raw batches:`, batches.map(b => ({ id: b.id, status: b.status, totalCalls: b.totalCalls })));
  
  const activeBatches = batches.filter(batch => batch.status !== 'cancelled');
  console.log(`🔍 TRIAL DEBUG: Active batches after filtering:`, activeBatches.map(b => ({ id: b.id, status: b.status, totalCalls: b.totalCalls })));
  
  const totalCalls = activeBatches.reduce((total, batch) => total + (batch.totalCalls || 0), 0);
  console.log(`🔍 TRIAL DEBUG: Final result - ${activeBatches.length} active batches with ${totalCalls} total calls`);
  return totalCalls;
};

/**
 * Validates trial user call limits only
 * @param {Array} existingBatches - Array of existing batch objects
 * @param {number} newCallsCount - Number of calls in the new batch being validated
 * @returns {Object} Validation result with canProceed, message, and details
 */
export const validateTrialUserLimits = (existingBatches, newCallsCount = 0) => {
  try {
    const activeBatchCount = countActiveBatches(existingBatches);
    const totalActiveCalls = getTotalActiveCalls(existingBatches);
    const totalCallsAfterNewBatch = totalActiveCalls + newCallsCount;

    const result = {
      canProceed: false,
      message: "",
      activeBatchCount,
      totalActiveCalls,
      totalCallsAfterNewBatch,
      callLimitExceeded: false
    };

    // Check total call limit (only constraint now)
    if (totalCallsAfterNewBatch > TRIAL_USER_PATIENT_LIMIT) {
      result.callLimitExceeded = true;
      const callsOverLimit = totalCallsAfterNewBatch - TRIAL_USER_PATIENT_LIMIT;
      const availableCalls = TRIAL_USER_PATIENT_LIMIT - totalActiveCalls;
      
      if (availableCalls > 0) {
        result.message = `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You currently have ${totalActiveCalls} active calls and can add ${availableCalls} more. Please reduce your batch size by ${callsOverLimit} call${callsOverLimit > 1 ? 's' : ''} or contact your administrator to upgrade your plan.`;
      } else {
        result.message = `You are on a trial plan limited to ${TRIAL_USER_PATIENT_LIMIT} total calls across all active batches. You currently have ${totalActiveCalls} active calls and have reached your limit. Please wait for existing batches to complete or contact your administrator to upgrade your plan.`;
      }
      return result;
    }

    // All checks passed
    result.canProceed = true;
    const remainingCalls = TRIAL_USER_PATIENT_LIMIT - totalCallsAfterNewBatch;
    result.message = `Ready to proceed. You will have ${totalCallsAfterNewBatch} total calls across ${activeBatchCount + 1} batch${activeBatchCount + 1 > 1 ? 'es' : ''} with ${remainingCalls} call${remainingCalls !== 1 ? 's' : ''} remaining.`;
    return result;

  } catch (error) {
    console.error('Error in validateTrialUserLimits:', error);
    return {
      canProceed: false,
      message: "Unable to validate trial limits. Please try again or contact your administrator.",
      activeBatchCount: 0,
      totalActiveCalls: 0,
      totalCallsAfterNewBatch: 0,
      callLimitExceeded: false
    };
  }
}; 